import React, { useState, useEffect } from "react";
import { Select, Spin, Slider, Button, Table } from "antd";
import { UserOutlined, SettingOutlined } from "@ant-design/icons";
import { postAuthMethod, getAuthMethod } from "../../../services/HttpServices";

const strategyOptions = [
  { value: "NO_ASSIGNMENT", label: "No Assignment (Shared Inbox)" },
  { value: "SELF_ASSIGNMENT", label: "Self Assignment (Assign to Campaign Creator)" },
  { value: "ROUND_ROBIN", label: "Round Robin (Equal Distribution)" },
  { value: "PERFORMANCE_BASED", label: "Performance Based (Smart Routing)" },
  { value: "MANUAL_WEIGHTED", label: "Manual Weighted (%)" }
];

const AssignmentStrategySelector = ({ 
    assignmentStrategy, 
    onStrategyChange, 
    assignmentWeights, 
    onWeightsChange,
    campaignId 
}) => {
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [previewData, setPreviewData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchTeamMembers();
    }, []);

    const fetchTeamMembers = async () => {
        try {
            const response = await getAuthMethod("/api/managed-users?sort=firstName,asc&size=10");
            if (response && response.data) {
                // Handle both direct array and paginated response formats just in case
                const users = Array.isArray(response.data) ? response.data : (response.data.content || []);
                setTeamMembers(users);
            }
        } catch (error) {
            console.error("Failed to fetch team members", error);
        }
    };

    const handleStrategyChange = (value) => {
        onStrategyChange(value);
        if (value === "NO_ASSIGNMENT" || value === "SELF_ASSIGNMENT") {
            onWeightsChange([]);
            setSelectedUsers([]);
            setPreviewData([]);
        }
    };

    const handleUserSelection = (userIds) => {
        setSelectedUsers(userIds);
        // Default weights for MANUAL
        if (assignmentStrategy === "MANUAL_WEIGHTED") {
            const defaultWeight = Math.floor(10000 / (userIds.length || 1));
            const newWeights = userIds.map(id => ({
                userId: id,
                assignmentWeight: defaultWeight
            }));
            onWeightsChange(newWeights);
        } else {
            const newWeights = userIds.map(id => ({
                userId: id,
                assignmentWeight: 0
            }));
            onWeightsChange(newWeights);
        }
    };

    const handleSliderChange = (userId, valueInPercent) => {
        const newValueBP = valueInPercent * 100;
        let newWeights = [...assignmentWeights];
        const changedIndex = newWeights.findIndex(w => w.userId === userId);
        const oldWeightBP = newWeights[changedIndex].assignmentWeight;
        
        if (oldWeightBP === newValueBP) return;
        
        const otherCount = newWeights.length - 1;
        if (otherCount === 0) {
            newWeights[changedIndex].assignmentWeight = 10000;
            onWeightsChange(newWeights);
            return;
        }

        const remBP = 10000 - newValueBP;
        const oldOtherSum = 10000 - oldWeightBP;

        let newSum = newValueBP;
        newWeights[changedIndex].assignmentWeight = newValueBP;

        for (let i = 0; i < newWeights.length; i++) {
            if (i === changedIndex) continue;
            
            let newOtherVal = 0;
            if (oldOtherSum === 0) {
                newOtherVal = Math.floor(remBP / otherCount);
            } else {
                newOtherVal = Math.floor(newWeights[i].assignmentWeight * (remBP / oldOtherSum));
            }
            newWeights[i].assignmentWeight = newOtherVal;
            newSum += newOtherVal;
        }

        // Handle rounding errors to ensure exactly 10000 basis points
        if (newSum !== 10000) {
            const diff = 10000 - newSum;
            const idxToFix = changedIndex === 0 ? 1 : 0;
            if (newWeights.length > 1) {
                newWeights[idxToFix].assignmentWeight += diff;
            }
        }

        onWeightsChange(newWeights);
    };

    const fetchPreview = async () => {
        if (!selectedUsers.length || assignmentStrategy === "NO_ASSIGNMENT" || assignmentStrategy === "SELF_ASSIGNMENT") return;
        
        setLoading(true);
        try {
            const payload = {
                assignmentStrategy,
                assignmentWeights: assignmentWeights.map(w => ({ userId: w.userId, assignmentWeight: w.assignmentWeight }))
            };
            const response = await postAuthMethod("/api/whatsapp-campaigns/assignment-preview", payload);
            if (response && response.data) {
                setPreviewData(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch preview", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (["ROUND_ROBIN", "PERFORMANCE_BASED"].includes(assignmentStrategy) && selectedUsers.length > 0) {
            fetchPreview();
        }
    }, [assignmentStrategy, selectedUsers]);

    const renderConfiguration = () => {
        if (assignmentStrategy === "NO_ASSIGNMENT") {
            return <div className="tw-text-sm tw-text-gray-500 tw-mt-2">Leads will remain unassigned in the shared inbox.</div>;
        }
        if (assignmentStrategy === "SELF_ASSIGNMENT") {
            return <div className="tw-text-sm tw-text-gray-500 tw-mt-2">Leads will be assigned to you automatically.</div>;
        }

        return (
            <div className="tw-mt-4 tw-space-y-4">
                <div>
                    <div className="tw-mb-2 tw-text-sm tw-font-medium tw-text-text-primary">
                        Select Team Members to Receive Leads
                    </div>
                    <Select
                        mode="multiple"
                        placeholder="Select agents"
                        className="tw-w-full"
                        value={selectedUsers}
                        onChange={handleUserSelection}
                        options={teamMembers.map(u => ({ label: u.firstName + " " + u.lastName, value: u.id }))}
                    />
                </div>

                {selectedUsers.length > 0 && assignmentStrategy === "MANUAL_WEIGHTED" && (
                    <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-border tw-border-gray-200">
                        <div className="tw-font-medium tw-mb-3">Set Assignment Percentages</div>
                        {assignmentWeights.map((weightObj) => {
                            const user = teamMembers.find(u => u.id === weightObj.userId);
                            const currentPercent = weightObj.assignmentWeight / 100;
                            return (
                                <div key={weightObj.userId} className="tw-flex tw-items-center tw-gap-4 tw-mb-2">
                                    <div className="tw-w-32 tw-text-sm tw-truncate">{user ? `${user.firstName} ${user.lastName}` : `User ${weightObj.userId}`}</div>
                                    <div className="tw-flex-1">
                                        <Slider 
                                            min={0} 
                                            max={100} 
                                            value={currentPercent} 
                                            onChange={(val) => handleSliderChange(weightObj.userId, val)} 
                                        />
                                    </div>
                                    <div className="tw-w-12 tw-text-sm tw-text-right">{currentPercent}%</div>
                                </div>
                            );
                        })}
                        <div className="tw-text-xs tw-text-gray-500 tw-mt-2">
                            Total: {(assignmentWeights.reduce((sum, w) => sum + w.assignmentWeight, 0) / 100).toFixed(0)}% 
                            <span className="tw-text-theme tw-ml-2">(Automatically balanced)</span>
                        </div>
                    </div>
                )}

                {selectedUsers.length > 0 && ["ROUND_ROBIN", "PERFORMANCE_BASED"].includes(assignmentStrategy) && (
                    <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg tw-border tw-border-gray-200">
                        <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
                            <div className="tw-font-medium">Calculated Assignment Distribution</div>
                            <Button size="small" type="link" onClick={fetchPreview} loading={loading}>Refresh Preview</Button>
                        </div>
                        {loading ? (
                            <div className="tw-text-center tw-py-4"><Spin /></div>
                        ) : (
                            <div className="tw-space-y-2">
                                {previewData.map((previewObj) => {
                                    const user = teamMembers.find(u => u.id === previewObj.userId);
                                    return (
                                        <div key={previewObj.userId} className="tw-flex tw-justify-between tw-text-sm">
                                            <span>{user ? `${user.firstName} ${user.lastName}` : `User ${previewObj.userId}`}</span>
                                            <span className="tw-font-semibold">{(previewObj.assignmentWeight / 100).toFixed(2)}%</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="tw-bg-white tw-p-5 tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm">
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
                <SettingOutlined className="tw-text-theme" />
                <h3 className="tw-text-base tw-font-bold tw-m-0">Lead Assignment Strategy</h3>
            </div>
            
            <div className="tw-mb-4">
                <div className="tw-mb-2 tw-text-sm tw-font-medium tw-text-text-primary">
                    How should inbound leads be routed?
                </div>
                <Select
                    value={assignmentStrategy}
                    onChange={handleStrategyChange}
                    className="tw-w-full"
                    options={strategyOptions}
                />
            </div>

            {renderConfiguration()}
        </div>
    );
};

export default AssignmentStrategySelector;
